import { inject, Injectable } from '@angular/core';
import { take } from 'rxjs/operators';
import { CreatorStateService } from './CreatorStateService';
import { Observable, of, Subject } from 'rxjs';
import { ISeatsPlan } from '../../interfaces';
import { CreatorActionsService } from './CreatorActionsService';
import { LeaveCreatorService } from './LeaveCreatorService';
import { AuditoriumCreatorService } from './AuditoriumCreatorService';
import { CreatorSteps } from '../../types';
import { Router } from '@angular/router';
import { NavigationService } from '@cinetixx/cinetixx-ui';
import { CreatorCacheService } from './CreatorCacheService';

@Injectable({
  providedIn: 'root'
})
export class CreatorNavigationService {

  private readonly _leaveCreatorService = inject(LeaveCreatorService);
  private readonly _auditoriumCreatorService = inject(AuditoriumCreatorService);
  private readonly _navigationService = inject(NavigationService);
  private readonly _router = inject(Router);

  public goTo(step: CreatorSteps): Observable<boolean> {
    const navigate$ = new Subject<boolean>();
    
    this._leaveCreatorService.canLeave()
      .pipe(take(1))
      .subscribe((canLeave) => {
        if (canLeave) {
          CreatorCacheService.clearCache();
          CreatorStateService.isLoading = true;
          CreatorStateService.pickedConfiguration = null;

          setTimeout(() => {
            let apiCall$: Observable<ISeatsPlan> = null;

            CreatorStateService.currentStep = step;
            CreatorStateService.clearCurrentState();

            if (step === 'seats-plan' || step === 'sector-plan') {
              apiCall$ = this._auditoriumCreatorService.loadSector(CreatorStateService.sectorId);
            } else if (step === 'connections') {
              apiCall$ = this._auditoriumCreatorService.loadCreatorConnections();
            } else if (step === 'zones') {
              apiCall$ = this._auditoriumCreatorService.loadCreatorZones();
            } else if (step === 'seat-priority') {
              apiCall$ = this._auditoriumCreatorService.loadCreatorSeatPriority();
            }

            apiCall$
              .pipe(take(1))
              .subscribe(() => {
                navigate$.next(true);
                navigate$.complete();
                CreatorActionsService.resetToDefault();

                if (step === 'sector-plan') {
                  this._navigationService.navigateByUrl(this._router.url.split('?')[0]);
                }
              });
          });
        } else {
          navigate$.next(false);
          navigate$.complete();
        }
      });

    return navigate$.asObservable();
  }

  public navigateTo(step: CreatorSteps): void {
    if (!CreatorStateService.isConfigured && CreatorStateService.parentSectorId && (step === 'zones' || step === 'connections')) {
      const firstSector = CreatorStateService.currentConfigurationSectors.at(0);
      CreatorStateService.movedToFirstSector = true;
      this._navigationService.navigateByUrl(`/account/administration/auditoriums/auditorium-creator/${CreatorStateService.parentSectorId}?sectorId=${firstSector.sectorId}`);
      CreatorStateService.currentStep = step;
    } else {
      this.getDataToView(step).subscribe(() => {
        CreatorStateService.currentStep = step;
        setTimeout(CreatorActionsService.resetToDefault.bind(this));
      });
    }
  }

  public getDataToView(step: CreatorSteps): Observable<ISeatsPlan> {
    if (step === 'connections') {
      return this._auditoriumCreatorService.loadCreatorConnections();
    } else if (step === 'zones') {
      return this._auditoriumCreatorService.loadCreatorZones();
    } else if (step === 'seat-priority') {
      return this._auditoriumCreatorService.loadCreatorSeatPriority();
    } else {
      return of(null);
    }
  }
}
